/**
 * audio-envelope.util — turn the COMBINED timeline audio into a compact, serializable
 * per-frame reactivity curve that beat overlays read so they PULSE WITH THE REAL SOUND
 * (bass / mids / voices / treble) instead of looping a synthetic sine.
 *
 * The envelope is computed ONCE on the main thread (from the same OfflineAudioContext
 * mixdown the beat detector analyses) and then:
 *   - PREVIEW: handed to the webav canvas compositor, sampled each drawn frame.
 *   - EXPORT:  attached to `RenderTimeline.audioEnvelope`, sampled each frame in the
 *     render worker.
 * Both paths import THIS module, so a beat reacts identically in preview and export.
 *
 * Framework-free (pure math + Float arrays) so the Web Worker can import it. All four
 * channels are normalised to 0..1 by their own peak, so a genuinely silent stretch
 * samples ~0 → the beat stays DORMANT, and the loudest moment samples ~1.
 */

/** Per-frame reactivity curves in ABSOLUTE timeline seconds (index = time / hopSeconds). */
export interface AudioEnvelope {
  /** Seconds between successive samples (≈ 1/60). */
  hopSeconds: number;
  /** Total analysed duration (seconds). */
  duration: number;
  /** Overall loudness (RMS), 0..1. */
  amp: number[];
  /** Low band < ~250 Hz (kick / bass), 0..1. */
  bass: number[];
  /** Mid band ~250 Hz–4 kHz (vocals / melody), 0..1. */
  mid: number[];
  /** High band > ~4 kHz (hats / air), 0..1. */
  treble: number[];
  /** Flattened log-frequency spectrum (`frame * spectrumBins + bin`), 0..1. */
  spectrum?: number[];
  /** Number of log-frequency values stored for each frame. */
  spectrumBins?: number;
  /** Positive spectral change, useful for transients and particle emission. */
  flux?: number[];
  /** Smoothed/thresholded transient strength, 0..1. */
  onset?: number[];
}

/** One resolved reactivity reading at a moment in time. */
export interface AudioSample {
  amp: number;
  bass: number;
  mid: number;
  treble: number;
  spectrum?: readonly number[];
  flux?: number;
  onset?: number;
}

/** The dormant reading — used before the first sample, past the end, or with no envelope. */
export const SILENT_AUDIO: AudioSample = { amp: 0, bass: 0, mid: 0, treble: 0 };

const FFT_WINDOW = 1024;      // power of two → radix-2 FFT
const BASS_HZ = 250;
const MID_HZ = 4000;
const SPECTRUM_BINS = 16;

/**
 * Build the reactivity envelope from a MONO signal. `fps` sets the sample density
 * (default 60 → one reading per 60fps frame). Cost is one 1024-pt FFT per frame; a
 * few hundred ms for a typical short-form timeline, run once off Detect Beats / export.
 */
export function computeAudioEnvelope(mono: Float32Array, sampleRate: number, fps = 60): AudioEnvelope {
  const hop = Math.max(1, Math.round(sampleRate / Math.max(1, fps)));
  const win = FFT_WINDOW;
  const half = win >> 1;
  const duration = mono.length / sampleRate;

  // Hann window to reduce spectral leakage in the per-frame FFT.
  const hann = new Float32Array(win);
  for (let i = 0; i < win; i++) hann[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (win - 1));

  const binHz = sampleRate / win;
  const bassMaxBin = Math.min(half, Math.max(1, Math.floor(BASS_HZ / binHz)));
  const midMaxBin = Math.min(half, Math.max(bassMaxBin + 1, Math.floor(MID_HZ / binHz)));

  const frames = Math.max(1, Math.ceil(mono.length / hop));
  const amp: number[] = new Array(frames);
  const bass: number[] = new Array(frames);
  const mid: number[] = new Array(frames);
  const treble: number[] = new Array(frames);
  const spectrum: number[] = new Array(frames * SPECTRUM_BINS).fill(0);
  const flux: number[] = new Array(frames).fill(0);
  const onset: number[] = new Array(frames).fill(0);

  const re = new Float32Array(win);
  const im = new Float32Array(win);
  const previousSpectrum = new Float32Array(SPECTRUM_BINS);
  const spectrumCounts = new Uint16Array(SPECTRUM_BINS);
  const minSpectrumHz = 45;
  const maxSpectrumHz = Math.min(sampleRate / 2, 16000);
  const logMin = Math.log(minSpectrumHz);
  const logSpan = Math.max(0.001, Math.log(maxSpectrumHz) - logMin);

  for (let f = 0; f < frames; f++) {
    const start = f * hop;

    // Amplitude = RMS over just the HOP (tighter to the transient than the whole window).
    let sq = 0;
    let n = 0;
    for (let i = start, end = Math.min(mono.length, start + hop); i < end; i++) { const s = mono[i]; sq += s * s; n++; }
    amp[f] = n ? Math.sqrt(sq / n) : 0;

    // Windowed FFT for the three frequency bands.
    for (let i = 0; i < win; i++) { const s = start + i; re[i] = (s < mono.length ? mono[s] : 0) * hann[i]; im[i] = 0; }
    fftRadix2(re, im);

    let b = 0;
    let m = 0;
    let t = 0;
    spectrumCounts.fill(0);
    for (let k = 1; k < half; k++) {
      const mag = Math.hypot(re[k], im[k]);
      if (k < bassMaxBin) b += mag;
      else if (k < midMaxBin) m += mag;
      else t += mag;

      const hz = k * binHz;
      if (hz >= minSpectrumHz && hz <= maxSpectrumHz) {
        const band = Math.min(SPECTRUM_BINS - 1, Math.max(0,
          Math.floor(((Math.log(hz) - logMin) / logSpan) * SPECTRUM_BINS)));
        spectrum[f * SPECTRUM_BINS + band] += mag;
        spectrumCounts[band]++;
      }
    }
    bass[f] = b;
    mid[f] = m;
    treble[f] = t;

    let positiveChange = 0;
    for (let band = 0; band < SPECTRUM_BINS; band++) {
      const index = f * SPECTRUM_BINS + band;
      const value = spectrumCounts[band] ? spectrum[index] / spectrumCounts[band] : 0;
      spectrum[index] = value;
      positiveChange += Math.max(0, value - previousSpectrum[band]);
      previousSpectrum[band] = value;
    }
    flux[f] = positiveChange / SPECTRUM_BINS;
  }

  normalizePeak(amp);
  normalizePeak(bass);
  normalizePeak(mid);
  normalizePeak(treble);
  normalizeSpectrumBands(spectrum, frames, SPECTRUM_BINS);
  normalizePeak(flux);

  // A short adaptive threshold keeps sustained notes from firing particles every frame,
  // while preserving genuine attacks. The result remains deterministic/offline-safe.
  for (let f = 0; f < frames; f++) {
    let local = 0;
    let count = 0;
    for (let i = Math.max(0, f - 10); i < f; i++) { local += flux[i]; count++; }
    const threshold = count ? (local / count) * 1.12 : 0;
    onset[f] = Math.max(0, flux[f] - threshold);
  }
  normalizePeak(onset);

  return {
    hopSeconds: hop / sampleRate,
    duration,
    amp,
    bass,
    mid,
    treble,
    spectrum,
    spectrumBins: SPECTRUM_BINS,
    flux,
    onset,
  };
}

/**
 * Sample the envelope at an absolute timeline time (linear interpolation between the two
 * nearest frames). Returns {@link SILENT_AUDIO} for a null/empty envelope or a time
 * outside the analysed range, so callers stay dormant where there is no sound.
 */
export function sampleAudioEnvelope(env: AudioEnvelope | null | undefined, tSeconds: number): AudioSample {
  if (!env || !env.amp.length || !(env.hopSeconds > 0)) return SILENT_AUDIO;
  const x = tSeconds / env.hopSeconds;
  if (!(x > 0)) return frameAt(env, 0);
  const last = env.amp.length - 1;
  if (x >= last) return frameAt(env, last);
  const i = Math.floor(x);
  const frac = x - i;
  return {
    amp: lerp(env.amp[i], env.amp[i + 1], frac),
    bass: lerp(env.bass[i], env.bass[i + 1], frac),
    mid: lerp(env.mid[i], env.mid[i + 1], frac),
    treble: lerp(env.treble[i], env.treble[i + 1], frac),
    spectrum: interpolateSpectrum(env, i, i + 1, frac),
    flux: interpolateOptional(env.flux, i, i + 1, frac),
    onset: interpolateOptional(env.onset, i, i + 1, frac),
  };
}

// ---------------------------------------------------------------------------
// internals
// ---------------------------------------------------------------------------

function frameAt(env: AudioEnvelope, i: number): AudioSample {
  return {
    amp: env.amp[i],
    bass: env.bass[i],
    mid: env.mid[i],
    treble: env.treble[i],
    spectrum: interpolateSpectrum(env, i, i, 0),
    flux: env.flux?.[i] ?? 0,
    onset: env.onset?.[i] ?? 0,
  };
}

function interpolateSpectrum(env: AudioEnvelope, a: number, b: number, t: number): number[] | undefined {
  const bins = env.spectrumBins ?? 0;
  const source = env.spectrum;
  if (!source || bins <= 0) return undefined;
  const out = new Array<number>(bins);
  const startA = a * bins;
  const startB = b * bins;
  for (let i = 0; i < bins; i++) out[i] = lerp(source[startA + i] ?? 0, source[startB + i] ?? 0, t);
  return out;
}

function interpolateOptional(values: number[] | undefined, a: number, b: number, t: number): number {
  return values ? lerp(values[a] ?? 0, values[b] ?? 0, t) : 0;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function normalizePeak(arr: number[]): void {
  const n = arr.length;
  if (!n) return;
  // Reference the 90th-percentile frame (a ROBUST peak), not the single loudest sample, so
  // the strongest ~10% of beats clip to full-scale and the reactivity reads as punchy
  // movement instead of a subtle wiggle dominated by one outlier transient. Silence → 0.
  const sorted = Array.prototype.slice.call(arr).sort((a: number, b: number) => a - b);
  let ref = sorted[Math.min(n - 1, Math.floor(n * 0.9))];
  if (!(ref > 0)) ref = sorted[n - 1]; // mostly-quiet → fall back to absolute max
  if (!(ref > 0)) return;              // truly silent → leave zeros (dormant)
  const inv = 1 / ref;
  for (let i = 0; i < n; i++) { const v = arr[i] * inv; arr[i] = v > 1 ? 1 : v; }
}

function normalizeSpectrumBands(values: number[], frames: number, bins: number): void {
  const bandValues = new Array<number>(frames);
  for (let band = 0; band < bins; band++) {
    for (let frame = 0; frame < frames; frame++) bandValues[frame] = values[frame * bins + band];
    normalizePeak(bandValues);
    for (let frame = 0; frame < frames; frame++) values[frame * bins + band] = bandValues[frame];
  }
}

/** In-place iterative radix-2 Cooley–Tukey FFT (length MUST be a power of two). */
function fftRadix2(re: Float32Array, im: Float32Array): void {
  const n = re.length;

  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i]; re[i] = re[j]; re[j] = tr;
      const ti = im[i]; im[i] = im[j]; im[j] = ti;
    }
  }

  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang);
    const wi = Math.sin(ang);
    const halfLen = len >> 1;
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      for (let k = 0; k < halfLen; k++) {
        const a = i + k;
        const b = a + halfLen;
        const vr = re[b] * cr - im[b] * ci;
        const vi = re[b] * ci + im[b] * cr;
        re[b] = re[a] - vr; im[b] = im[a] - vi;
        re[a] += vr; im[a] += vi;
        const ncr = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = ncr;
      }
    }
  }
}
